import React, { useState } from 'react';
import {
  Film,
  Cloud,
  KeyRound,
  Radio,
  Printer,
  ArrowUpRight,
  CheckCircle2,
} from 'lucide-react';
import { services } from '../mock';
import { useSite } from '../context/SiteContext';
import { useLang } from '../context/LangContext';
import { pickI18n } from '../i18n/helpers';
import AnimatedBackground from './AnimatedBackground';

const iconMap = { Film, Cloud, KeyRound, Radio, Printer };

const Services = () => {
  const { services: srv } = useSite();
  const { t, lang } = useLang();
  const list = srv && srv.length ? srv : services;
  const [active, setActive] = useState(0);
  const current = list[active] || list[0];
  if (!current) return null;
  const Icon = iconMap[current.icon] || Film;
  const P = (v) => pickI18n(v, lang);

  return (
    <section id="servicios" className="relative py-20 sm:py-28 bg-[#0d1b2a] overflow-hidden">
      <AnimatedBackground variant="aurora" intensity={0.3} />
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(0,224,199,1) 1px, transparent 1px)',
          backgroundSize: '100% 5rem',
        }}
      />

      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 relative">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-12 sm:mb-16">
          <div className="max-w-2xl">
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#00e0c7] mb-4">
              {t('sections.services')}
            </div>
            <h2 className="font-display text-white text-2xl sm:text-3xl lg:text-[52px] font-light tracking-tight" style={{ lineHeight: 1.25 }}>
              {t('sections.servicesTitle')}{' '}
              <span className="italic text-[#5eead4]">{t('sections.servicesHighlight')}</span>
              {t('sections.servicesTitleEnd')}
            </h2>
          </div>
          <div className="text-white/40 text-sm font-mono">
            {t('sections.servicesCounter')}
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-8">
          {/* Service list */}
          <div className="lg:col-span-5 space-y-2">
            {list.map((s, i) => {
              const ItemIcon = iconMap[s.icon] || Film;
              const isActive = active === i;
              return (
                <button
                  key={s.id}
                  onClick={() => setActive(i)}
                  className={`w-full text-left p-5 rounded-2xl border transition-all duration-300 ${
                    isActive
                      ? 'bg-[#00e0c7]/[0.06] border-[#00e0c7]/40'
                      : 'bg-white/[0.02] border-white/5 hover:border-white/15'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div
                      className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${
                        isActive
                          ? 'bg-[#00e0c7] text-[#0d1b2a]'
                          : 'bg-white/5 text-[#00e0c7]'
                      }`}
                    >
                      <ItemIcon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] text-white/40 font-mono">{s.code}</span>
                      </div>
                      <h3 className="text-white font-medium text-[16px] leading-tight truncate">
                        {P(s.title)}
                      </h3>
                      <p className="text-white/45 text-[12.5px] mt-1 line-clamp-1">
                        {P(s.short)}
                      </p>
                    </div>
                    <ArrowUpRight
                      className={`w-4 h-4 flex-shrink-0 transition-all ${
                        isActive ? 'text-[#00e0c7] rotate-45' : 'text-white/30'
                      }`}
                    />
                  </div>
                </button>
              );
            })}
          </div>

          {/* Active service detail */}
          <div className="lg:col-span-7 lg:sticky lg:top-28 h-fit">
            <div className="relative rounded-3xl overflow-hidden border border-white/5 bg-[#0a1420]">
              <div className="relative h-72 lg:h-80 overflow-hidden">
                <img
                  key={current.image}
                  src={current.image}
                  alt={current.title}
                  className="w-full h-full object-cover opacity-75 animate-[fadeIn_0.5s_ease]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a1420] via-[#0a1420]/30 to-transparent" />
                <div className="absolute top-6 left-6 flex items-center gap-3">
                  <div className="w-14 h-14 rounded-2xl bg-[#00e0c7] flex items-center justify-center">
                    <Icon className="w-6 h-6 text-[#0d1b2a]" />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#00e0c7]">
                      {current.code} · Servicio
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-8 lg:p-10">
                <h3 className="font-display text-white text-2xl lg:text-3xl font-light tracking-tight leading-tight mb-5">
                  {P(current.title)}
                </h3>
                <p className="text-white/65 text-[15px] leading-[1.75] mb-7">
                  {P(current.description)}
                </p>
                <div className="flex flex-wrap gap-2 mb-8">
                  {(current.features || []).map((f, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-[12px] text-white/70"
                    >
                      <CheckCircle2 className="w-3 h-3 text-[#00e0c7]" />
                      {P(f)}
                    </span>
                  ))}
                </div>

                <button
                  onClick={() =>
                    document.querySelector('#contacto')?.scrollIntoView({ behavior: 'smooth' })
                  }
                  className="group inline-flex items-center gap-2 px-6 h-12 rounded-full bg-white/5 hover:bg-[#00e0c7] hover:text-[#0d1b2a] text-white border border-white/10 hover:border-[#00e0c7] text-sm font-medium transition-all"
                >
                  {t('cta.requestService')}
                  <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
